/**
 * Sprint E · #10 · PendingReviewSection
 *
 * Banner + lista interactiva de reservas online que requieren validación manual
 * del titular/gestor. Muestra cada cita con 3 acciones:
 *   - Aceptar    → POST /api/appointments/{id}/accept
 *   - Rechazar   → POST /api/appointments/{id}/reject  { reason }   (modal)
 *   - Solicitar info → POST /api/appointments/{id}/request-info { message } (modal, abre chat)
 */
import { useEffect, useState } from "react";
import { api, formatErr, fmtEur } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Warning, CheckCircle, XCircle, ChatCircleDots, Clock, CarSimple, User as UserIcon, Tag } from "@phosphor-icons/react";

export function PendingReviewSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);  // appointment id
  const [infoFor, setInfoFor] = useState(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments/pending-review");
      setItems(data || []);
    } catch (e) {
      // 403 si el user no es admin/gestor → silenciamos
      if (e?.response?.status !== 403) {
        toast.error(formatErr(e?.response?.data?.detail));
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onAccept = async (it) => {
    setBusy(true);
    try {
      await api.post(`/appointments/${it.id}/accept`);
      toast.success("Reserva aceptada y cliente notificado");
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Indica un motivo"); return; }
    setBusy(true);
    try {
      await api.post(`/appointments/${rejectFor}/reject`, { reason: reason.trim() });
      toast.success("Reserva rechazada · cliente notificado");
      setRejectFor(null);
      setReason("");
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitRequestInfo = async () => {
    if (!message.trim()) { toast.error("Escribe un mensaje"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${infoFor}/request-info`, { message: message.trim() });
      toast.success(data?.via === "chat" ? "Mensaje enviado al chat del cliente" : "Email enviado al cliente");
      setInfoFor(null);
      setMessage("");
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="ag-card p-5 border-orange-300 bg-orange-50/40" data-testid="pending-review-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-orange-100 grid place-items-center">
            <Warning size={18} weight="fill" className="text-orange-600" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-700 font-semibold">Sprint E · Pendientes de validación</div>
            <h3 className="font-display text-lg md:text-xl font-bold tracking-tight text-[#1A1A1A]" data-testid="pending-review-title">
              {items.length} reserva{items.length === 1 ? "" : "s"} esperando tu respuesta
            </h3>
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="pending-review-list">
        {items.map((it) => (
          <div key={it.id} className="border border-orange-200 bg-white rounded-sm p-4 flex flex-wrap gap-4 items-start"
               data-testid={`pending-review-item-${it.id}`}>
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-semibold">
                  <Warning size={10} weight="fill" /> Validar
                </span>
                <span className="text-sm font-medium text-[#1A1A1A] truncate">{it.wash_type}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                <span className="flex items-center gap-1.5"><Clock size={12} /> {new Date(it.date).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</span>
                <span className="flex items-center gap-1.5"><UserIcon size={12} /> {it.customer_name || "—"}</span>
                <span className="flex items-center gap-1.5"><CarSimple size={12} /> <b className="font-mono-tab">{it.license_plate}</b></span>
                {it.total_price != null && (
                  <span className="flex items-center gap-1.5"><Tag size={12} /> {fmtEur(it.total_price)}</span>
                )}
              </div>
              {it.notes && (
                <div className="text-xs text-gray-500 italic pt-1">"{it.notes}"</div>
              )}
            </div>
            <div className="flex gap-1.5 flex-wrap" data-testid={`pending-review-actions-${it.id}`}>
              <Button size="sm" disabled={busy} onClick={() => onAccept(it)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8 text-xs"
                      data-testid={`pending-accept-${it.id}`}>
                <CheckCircle size={13} weight="fill" className="mr-1" /> Aceptar
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => { setInfoFor(it.id); setMessage(""); }}
                      className="border-sky-500 text-sky-700 hover:bg-sky-50 rounded-sm h-8 text-xs"
                      data-testid={`pending-info-${it.id}`}>
                <ChatCircleDots size={13} weight="bold" className="mr-1" /> Solicitar info
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => { setRejectFor(it.id); setReason(""); }}
                      className="border-red-500 text-red-700 hover:bg-red-50 rounded-sm h-8 text-xs"
                      data-testid={`pending-reject-${it.id}`}>
                <XCircle size={13} weight="bold" className="mr-1" /> Rechazar
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Rechazar */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <XCircle size={18} weight="bold" className="text-red-600" /> Rechazar reserva
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-xs text-gray-600">
              Indica un motivo. El cliente recibirá un email con esta explicación.
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Ej. No tenemos disponibilidad ese día. Te podemos atender la semana siguiente…"
              className="rounded-sm"
              data-testid="reject-reason-input"
            />
            <div className="text-[10px] text-[var(--ag-text-muted)] text-right">{reason.length}/500</div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejectFor(null)} disabled={busy} data-testid="reject-cancel">Cancelar</Button>
            <Button onClick={submitReject} disabled={busy || !reason.trim()}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="reject-confirm">
              {busy ? "Enviando…" : "Rechazar y notificar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Solicitar info */}
      <Dialog open={!!infoFor} onOpenChange={(o) => !o && setInfoFor(null)}>
        <DialogContent className="max-w-md" data-testid="request-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ChatCircleDots size={18} weight="bold" className="text-sky-600" /> Solicitar más información
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-xs text-gray-600">
              Se enviará por chat interno si el cliente tiene cuenta; si no, por email. La reserva sigue pendiente.
            </p>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={1500}
              placeholder="Ej. ¿Podrías enviarnos fotos del daño en el cristal? Necesitamos confirmar medidas y modelo."
              className="rounded-sm"
              data-testid="request-info-message-input"
            />
            <div className="text-[10px] text-[var(--ag-text-muted)] text-right">{message.length}/1500</div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInfoFor(null)} disabled={busy} data-testid="request-info-cancel">Cancelar</Button>
            <Button onClick={submitRequestInfo} disabled={busy || !message.trim()}
                    className="bg-sky-600 hover:bg-sky-700 text-[var(--ag-text)]"
                    data-testid="request-info-confirm">
              {busy ? "Enviando…" : "Enviar mensaje"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
